import React, { useCallback, useEffect, useRef, useState } from 'react';
import { marcheIa as api } from '../services/api';
import './MarcheAnalyseIa.css';

// ─── Analyse IA du marché — panneau latéral du rapport Marché ────────────────
// Génère (admin) une analyse sur le périmètre des filtres — période et
// catégorie VP/VUL ; les marchés analysés sont toujours la Martinique et la
// Guadeloupe — et relit l'historique (tous). La génération tourne côté serveur :
// le panneau relit l'analyse toutes les 2 s et affiche le texte au fil de
// l'eau. « Aperçu du dossier » montre ce qui partirait vers l'API, sans appel.

// Rendu Markdown minimal (titres, gras, italique, listes, tableaux) — même
// principe que l'analyse VN de Finance : le texte vient de notre backend, le
// HTML reste sous contrôle (échappement d'abord).
export function mdVersHtml(md) {
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lignes = esc(md || '').split('\n');
  const out = []; let enTable = false; let enListe = false;
  const fermer = () => { if (enTable) { out.push('</tbody></table>'); enTable = false; } if (enListe) { out.push('</ul>'); enListe = false; } };
  const inline = (t) => t.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/(^|[^*])\*([^*]+)\*/g, '$1<i>$2</i>');
  for (let i = 0; i < lignes.length; i += 1) {
    const l = lignes[i];
    if (/^\s*\|/.test(l)) {
      const cellules = l.replace(/^\s*\||\|\s*$/g, '').split('|').map((c) => c.trim());
      if (cellules.every((c) => /^:?-{2,}:?$/.test(c))) continue;
      if (!enTable) { fermer(); out.push('<table class="ia-table"><tbody>'); enTable = true; }
      const tag = (i + 1 < lignes.length && /^\s*\|[\s:|-]+\|\s*$/.test(lignes[i + 1])) ? 'th' : 'td';
      out.push(`<tr>${cellules.map((c) => `<${tag}>${inline(c)}</${tag}>`).join('')}</tr>`);
      continue;
    }
    if (/^\s*[-*] /.test(l)) {
      if (!enListe) { fermer(); out.push('<ul>'); enListe = true; }
      out.push(`<li>${inline(l.replace(/^\s*[-*] /, ''))}</li>`);
      continue;
    }
    fermer();
    const h = l.match(/^(#{1,4}) (.*)/);
    if (h) { out.push(`<h${h[1].length + 1}>${inline(h[2])}</h${h[1].length + 1}>`); continue; }
    if (/^\s*-{3,}\s*$/.test(l)) { out.push('<hr />'); continue; }
    if (l.trim() === '') continue;
    out.push(`<p>${inline(l)}</p>`);
  }
  fermer();
  return out.join('\n');
}

export const dateHeure = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
// Coût au tarif public Anthropic (USD, réflexion comprise).
export const usd = (v) => `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
export const STATUT = { en_cours: 'en cours…', termine: 'terminée', tronque: 'tronquée', echec: 'échec' };

export default function MarcheAnalyseIa({ filtres, libellePerimetre, onFermer }) {
  const [liste, setListe] = useState(null);
  const [editable, setEditable] = useState(false);
  const [courante, setCourante] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [attente, setAttente] = useState(false);
  const minuteur = useRef(null);

  const chargerListe = useCallback(() => api.liste().then((r) => { setListe(r.data.analyses); setEditable(r.data.editable); return r.data.analyses; }), []);
  const ouvrir = useCallback((id) => {
    clearTimeout(minuteur.current);
    api.lire(id).then((r) => {
      setCourante(r.data); setApercu(null);
      if (r.data.statut === 'en_cours') minuteur.current = setTimeout(() => ouvrir(id), 2000);
      else chargerListe();
    }).catch((e) => setErreur(e.response?.data?.error || 'Analyse introuvable'));
  }, [chargerListe]);

  useEffect(() => {
    chargerListe().then((l) => { if (l && l.length) ouvrir(l[0].id); }).catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement'));
    return () => clearTimeout(minuteur.current);
  }, [chargerListe, ouvrir]);

  const generer = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.lancer(filtres); await chargerListe(); ouvrir(r.data.id); }
    catch (e) { setErreur(e.response?.data?.error || 'Génération impossible'); }
    finally { setAttente(false); }
  };
  const voirApercu = async () => {
    setErreur(''); setAttente(true);
    try { const r = await api.apercu(filtres); setApercu(r.data); setCourante(null); }
    catch (e) { setErreur(e.response?.data?.error || 'Aperçu impossible'); }
    finally { setAttente(false); }
  };
  const exporter = () => {
    const b = new Blob([courante.contenu || ''], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(b);
    a.download = `analyse-marche-${String(courante.cree_le).slice(0, 10)}.md`; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="ia-voile" onClick={onFermer}>
      <aside className="ia-panneau" onClick={(e) => e.stopPropagation()} aria-label="Analyse IA du marché">
        <div className="ia-tete">
          <div>
            <h2>Analyse IA du marché</h2>
            <div className="ia-sous">Martinique et Guadeloupe · AUBERY et ses 3 principaux concurrents par département · {libellePerimetre}</div>
          </div>
          <button type="button" className="ia-fermer" onClick={onFermer} aria-label="Fermer" title="Fermer">✕</button>
        </div>

        <div className="ia-actions">
          {editable && <button type="button" className="ia-btn ia-btn-p" disabled={attente || courante?.statut === 'en_cours'} onClick={generer}>Générer une analyse</button>}
          {editable && <button type="button" className="ia-btn" disabled={attente} onClick={voirApercu}>Aperçu du dossier</button>}
          {(courante?.statut === 'termine' || courante?.statut === 'tronque') && <button type="button" className="ia-btn" onClick={exporter}>⬇ Markdown</button>}
          {!editable && <span className="ia-note">La génération est réservée aux administrateurs ; l'historique est consultable par tous.</span>}
        </div>
        {erreur && <div className="ia-erreur">⚠️ {erreur}</div>}

        <div className="ia-corps">
          <div className="ia-histo">
            <div className="ia-histo-t">Historique</div>
            {liste === null ? <div className="ia-note">Chargement…</div> : !liste.length ? <div className="ia-note">Aucune analyse pour l'instant.</div> : liste.map((a) => (
              <button type="button" key={a.id} className={`ia-histo-l${courante?.id === a.id ? ' on' : ''}`} onClick={() => ouvrir(a.id)}>
                <b>{dateHeure(a.cree_le)}</b>
                <span>{a.periode} · {a.categorie}</span>
                {a.cout_usd != null && <span>{usd(a.cout_usd)}</span>}
                <span className={`ia-statut ia-${a.statut}`}>{STATUT[a.statut] || a.statut}</span>
              </button>
            ))}
          </div>

          <div className="ia-contenu">
            {apercu ? (
              <>
                <div className="ia-meta">Aperçu — rien n'a été envoyé · {apercu.modele} · prompt {apercu.version_prompt} · {Math.round(apercu.caracteres / 1000)} k caractères (≈ {Math.round(apercu.caracteres / 3200)} k tokens)</div>
                <details open><summary>Consigne système</summary><pre>{apercu.system}</pre></details>
                <details><summary>Message et dossier chiffré</summary><pre>{apercu.user}</pre></details>
              </>
            ) : !courante ? (
              <div className="ia-note">{editable ? 'Générez une analyse, ou choisissez-en une dans l\'historique.' : 'Choisissez une analyse dans l\'historique.'}</div>
            ) : (
              <>
                <div className="ia-meta">
                  {dateHeure(courante.cree_le)} · {courante.auteur} · {courante.periode} · {courante.modele} · prompt {courante.version_prompt}
                  {courante.statut !== 'en_cours' && courante.tokens_entree ? ` · ${Math.round(courante.tokens_entree / 1000)} k tokens lus, ${Math.round((courante.tokens_sortie || 0) / 1000)} k écrits · ${Math.round((courante.duree_ms || 0) / 1000)} s` : ''}
                  {courante.cout_usd != null ? <> · <b>{usd(courante.cout_usd)}</b></> : null}
                </div>
                {(courante.statut === 'echec' || courante.statut === 'tronque') && <div className="ia-erreur">⚠️ {courante.erreur}</div>}
                {courante.statut === 'en_cours' && <div className="ia-cours">Analyse en cours de rédaction… {courante.contenu ? '' : 'Le modèle étudie le dossier (une à deux minutes).'}</div>}
                <div className="ia-rapport" dangerouslySetInnerHTML={{ __html: mdVersHtml(courante.contenu) }} />
              </>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
